import React from 'react';
import './RouteItinerary.css';

interface Checkpoint {
  tipo: string;
  descricao: string;
  distancia_km: number;
  tempo_minutos: number;
  horario_chegada?: string;
  horario_saida?: string;
  duracao_pausa_minutos?: number;
  coordenadas: [number, number];
  rodovia?: string;
  valor?: number;
  obrigatoria?: boolean;
  tempo_conducao_acumulado?: number;
  tempo_jornada_total?: number;
}

interface RouteItineraryProps {
  checkpoints: Checkpoint[];
  horarioInicio?: string;
}

const RouteItinerary: React.FC<RouteItineraryProps> = ({ checkpoints, horarioInicio }) => {
  
  const formatarHorario = (isoDate: string | undefined) => {
    if (!isoDate) return '--:--';
    const date = new Date(isoDate);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatarData = (isoDate: string | undefined) => {
    if (!isoDate) return '--';
    const date = new Date(isoDate);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const formatarTempo = (minutos: number) => {
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return `${horas}h${mins.toString().padStart(2, '0')}`;
  };

  const getIconeCheckpoint = (tipo: string) => {
    const icones: { [key: string]: string } = {
      'origem': '🏁',
      'coleta': '📦',
      'entrega': '🎯',
      'pausa': '☕',
      'pausa_obrigatoria': '⏸️',
      'pedagio': '💰',
      'encerramento_jornada': '🛌'
    };
    return icones[tipo] || '📍';
  };

  const getCorTipo = (tipo: string) => {
    const cores: { [key: string]: string } = {
      'origem': '#28a745',
      'coleta': '#007bff',
      'entrega': '#17a2b8',
      'pausa': '#ffc107',
      'pausa_obrigatoria': '#dc3545',
      'pedagio': '#fd7e14',
      'encerramento_jornada': '#6c757d'
    };
    return cores[tipo] || '#6c757d';
  };

  const getTipoLabel = (tipo: string) => {
    const labels: { [key: string]: string } = {
      'origem': 'ORIGEM',
      'coleta': 'COLETA',
      'entrega': 'ENTREGA',
      'pausa': 'PAUSA',
      'pausa_obrigatoria': 'PAUSA OBRIGATÓRIA',
      'pedagio': 'PEDÁGIO',
      'encerramento_jornada': 'ENCERRAMENTO JORNADA'
    };
    return labels[tipo] || tipo.toUpperCase();
  };

  return (
    <div className="route-itinerary">
      <div className="itinerary-header">
        <h3>📋 Itinerário Detalhado</h3>
        <div className="itinerary-stats">
          <span>Total de pontos: {checkpoints.length}</span>
          <span>Pedágios: {checkpoints.filter(cp => cp.tipo === 'pedagio').length}</span>
          <span>Pausas: {checkpoints.filter(cp => cp.tipo === 'pausa_obrigatoria').length}</span>
        </div>
      </div>

      <div className="itinerary-timeline">
        {checkpoints.map((checkpoint, index) => (
          <div 
            key={index} 
            className={`itinerary-item ${checkpoint.tipo} ${checkpoint.obrigatoria ? 'obrigatoria' : ''}`}
          >
            <div className="item-marker">
              <div 
                className="marker-icon" 
                style={{ backgroundColor: getCorTipo(checkpoint.tipo) }}
              >
                <span>{getIconeCheckpoint(checkpoint.tipo)}</span>
              </div>
              {index < checkpoints.length - 1 && <div className="marker-line"></div>}
            </div>

            <div className="item-content">
              <div className="item-header">
                <span 
                  className="item-tipo" 
                  style={{ backgroundColor: getCorTipo(checkpoint.tipo) }}
                >
                  {getTipoLabel(checkpoint.tipo)}
                </span>
                <span className="item-distancia">
                  {checkpoint.distancia_km}km
                </span>
              </div>

              <h4 className="item-descricao">{checkpoint.descricao}</h4>

              <div className="item-horarios">
                {checkpoint.horario_chegada && (
                  <div className="horario-info">
                    <span className="horario-label">Chegada:</span>
                    <span className="horario-valor">
                      {formatarData(checkpoint.horario_chegada)} {formatarHorario(checkpoint.horario_chegada)}
                    </span>
                  </div>
                )}

                {checkpoint.duracao_pausa_minutos && (
                  <div className="horario-info pausa">
                    <span className="horario-label">Duração:</span>
                    <span className="horario-valor">
                      {formatarTempo(checkpoint.duracao_pausa_minutos)}
                    </span>
                  </div>
                )}

                {checkpoint.horario_saida && (
                  <div className="horario-info">
                    <span className="horario-label">Saída:</span>
                    <span className="horario-valor">
                      {formatarHorario(checkpoint.horario_saida)}
                    </span>
                  </div>
                )}
              </div>

              {checkpoint.tipo === 'pedagio' && checkpoint.valor && (
                <div className="item-extra pedagio-valor">
                  💵 Valor: R$ {checkpoint.valor.toFixed(2)}
                  {checkpoint.rodovia && ` - ${checkpoint.rodovia}`}
                </div>
              )}

              {checkpoint.obrigatoria && (
                <div className="item-extra obrigatoria-info">
                  ⚠️ Parada obrigatória - Lei 13.103/2015
                </div>
              )}

              {checkpoint.tempo_conducao_acumulado !== undefined && (
                <div className="item-stats">
                  <span>Tempo direção: {formatarTempo(checkpoint.tempo_conducao_acumulado)}</span>
                  {checkpoint.tempo_jornada_total !== undefined && (
                    <span>Tempo jornada: {formatarTempo(checkpoint.tempo_jornada_total)}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="itinerary-footer">
        <div className="legenda">
          <h4>Legenda:</h4>
          <div className="legenda-items">
            <span><span className="leg-icon" style={{background: '#28a745'}}>🏁</span> Origem/Destino</span>
            <span><span className="leg-icon" style={{background: '#007bff'}}>📦</span> Coleta</span>
            <span><span className="leg-icon" style={{background: '#17a2b8'}}>🎯</span> Entrega</span>
            <span><span className="leg-icon" style={{background: '#ffc107'}}>☕</span> Pausa</span>
            <span><span className="leg-icon" style={{background: '#dc3545'}}>⏸️</span> Pausa Obrigatória</span>
            <span><span className="leg-icon" style={{background: '#fd7e14'}}>💰</span> Pedágio</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteItinerary;
